import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authService } from '../services/authService';

export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      _hasHydrated: false,

      login: async (email, password) => {
        try {
          const data = await authService.login(email, password);
          
          // Handle both 'token' and 'access_token' from API
          const token = data.token || data.access_token;
          
          // Verify data has token and user
          if (token && data.user) {
            set({
              user: data.user,
              token: token,
              isAuthenticated: true,
            });
            
            // Double-check localStorage was set by authService
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            
            if (!storedToken || !storedUser) {
              // Set it manually as fallback
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(data.user));
            }
            
            return { success: true };
          } else {
            return {
              success: false,
              error: 'Invalid response from server - missing token or user',
            };
          }
        } catch (error) {
          return {
            success: false,
            error:
              error.userMessage ||
              error.response?.data?.message ||
              error.response?.data?.error ||
              error.message ||
              'Login failed',
          };
        }
      },

      register: async (userData) => {
        try {
          const data = await authService.register(userData);
          
          // Handle both 'token' and 'access_token' from API
          const token = data.token || data.access_token;
          
          // Verify data has token and user
          if (token && data.user) {
            set({
              user: data.user,
              token: token,
              isAuthenticated: true,
            });
            
            // Double-check localStorage was set by authService
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            
            if (!storedToken || !storedUser) {
              // Set it manually as fallback
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(data.user));
            }
            
            return { success: true };
          } else {
            return {
              success: false,
              error: 'Invalid response from server - missing token or user',
            };
          }
        } catch (error) {
          return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.error || error.message || 'Registration failed',
          };
        }
      },

      logout: async () => {
        await authService.logout();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      initialize: () => {
        const token = authService.getToken();
        const user = authService.getStoredUser();
        if (token && user) {
          set({
            user,
            token,
            isAuthenticated: true,
          });
        }
      },

      setUser: (user) => {
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          set({ user });
        }
      },

      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error && state) {
            state.setHasHydrated(true);
            // Sync Zustand state with localStorage after rehydration
            const token = localStorage.getItem('token');
            const userStr = localStorage.getItem('user');
            if (token && userStr) {
              try {
                const user = JSON.parse(userStr);
                state.user = user;
                state.token = token;
                state.isAuthenticated = true;
              } catch (e) {
                // Invalid user data, clear it
                localStorage.removeItem('token');
                localStorage.removeItem('user');
              }
            }
          }
        };
      },
    }
  )
);
